import useBookingSummary from "@/composables/useBookingSummary";
import i18n from "@/config/i18n";
import { groupBy } from "@/helpers/groupByHelper";
import { CalculatePriceResponse } from "@/models/api/CalculatePriceResponse";
import { CancellationTypeEnum, ModeOfTravelEnum } from "@/models/enums";
import {
    BookingDialogResponse,
    BookingItemResponse,
    BookingSectionResponse
} from "../../api/BookingSummaryResponse";

export class BookingSummary {
    bookingReference?: string;
    bookingNumber?: number;
    modeOfTravel: string;
    isAccommodation: boolean;
    serviceType: string;
    passengers: BookingItem[];
    bookingSections: BookingSummarySection[];
    extras: BookingItem[];
    totalPrice: number;
    leftToPay: number;
    paid: number;
    amd: number;
    currency: string;
    campaignName: string;
    isEditable: boolean;
    customerName: string;
    customerEmail: string;
    names: string[];
    cancellationType?: CancellationTypeEnum;
    checkInUrl?: string;

    constructor(resp: CalculatePriceResponse) {
        this.passengers = [];
        this.extras = [];

        this.extras = resp.extras?.map(e => new BookingItem(e));

        if (resp.bookingSections.length > 0) {
            const items = getItems(resp.bookingSections[0].items);
            this.passengers = getPassengers(items);
        }

        this.bookingReference = resp.bookingReference ?? "";
        this.bookingNumber = resp.bookingNumber > 0 ? resp.bookingNumber : undefined;
        this.modeOfTravel = typeof resp.modeOfTravel !== 'undefined' ? i18n.global.t(`bookingType.${ModeOfTravelEnum[resp.modeOfTravel]}`) : "";
        this.isAccommodation = resp.isAccommodation;
        this.serviceType = resp.bookingSections.length > 1 ? i18n.global.t("serviceType.return") : i18n.global.t("serviceType.oneWay");
        this.bookingSections = resp.bookingSections.map((x) => new BookingSummarySection(x));
        this.totalPrice = resp.totalPrice ?? 0;
        this.leftToPay = resp.leftToPay ?? 0;
        this.paid = resp.paid ?? 0;
        this.amd = resp.amendmentFee ?? 0;
        this.currency = resp.currency ? i18n.global.t(`currency.${resp.currency}`) : i18n.global.t("currency.GBP");
        this.campaignName = resp.campaignName;
        this.isEditable = resp.isEditable;
        this.customerName = resp.customerName ?? "";
        this.customerEmail = resp.customerEmail ?? "";
        this.names = resp.passengers ?? [];
        this.cancellationType = resp.cancellationType;
        this.checkInUrl = resp.checkInUrl;
    }
}

export class BookingSummarySection {
    departureDate: string;
    travelType: ModeOfTravelEnum;
    arrivalDate: string;
    route: string;
    routeText: string;
    price?: number;
    flexibility: string;
    items?: BookingItem[];
    discount?: number;
    icon: string;
		disabled: boolean;
	    
    constructor(resp: BookingSectionResponse) {
        const items = getItems(resp.items);      
        this.departureDate = resp.departureDateTime;
        this.arrivalDate = resp.arrivalDateTime;
        this.items = items; 
        this.route = resp.route;
        this.routeText = getRouteTranslations(resp.route, resp.travelType) ?? "";
        this.price = resp.price;
        this.discount = resp.discount;
        this.flexibility = resp.flexibility;
        this.travelType = resp.travelType;
        this.disabled = resp.isLocked;
        this.icon = resp.travelType === ModeOfTravelEnum.Scillonian ? "vessel-ship" : "vessel-aircraft";
        
    }
}

export class BookingItem {
    text: string;
    price?: number;
    amount: number;
    icon: string;
    code: string;
    group: string;
    name: string;
    type: string;

    constructor(item: BookingItemResponse, dialog?: BookingDialogResponse) {
        
        const amount = getAmount(item, dialog);

        this.code = item.name;
        this.text = item.group === 'P' && dialog?.type ? i18n.global.t(`passenger.${dialog.type}`, amount)
        : i18n.global.t(`basket.resources.description.${item.name}`) ?? "";
        this.price = getPrice(item, dialog);
        this.amount = getAmount(item, dialog);
        this.group = item.group;
        this.name = dialog?.name ?? "";
        this.icon = 'cart/' + (item.name === 'LC' ? 'lc' : (item.group ?? "").toLowerCase());
        this.type = dialog?.type ?? "";
    }
}

const getAmount = (item: BookingItemResponse, dialog?: BookingDialogResponse) => {
    return (dialog?.amount ?? 0 > 0 ? dialog?.amount : item.amount) ?? 0;
}

const getPrice = (item: BookingItemResponse, dialog?: BookingDialogResponse) => {
    return (dialog?.price ?? 0 > 0 ? dialog?.price : item.price) ?? 0;
}

const getItems = (items: BookingItemResponse[]): BookingItem[] => {
    return items.flatMap(i => i.items.length > 0 ? i.items.map(dialog => new BookingItem(i, dialog)) : [new BookingItem(i)])
}

const getPassengers = (items: BookingItem[]): BookingItem[] => {
    const passengers = items.filter(i => ["PAX"].includes(i.code));
    const passengerList = groupBy(passengers, "type");

    const passengerItems = [];
    for (const key in passengerList) {
        const resource = passengerList[key] as BookingItem[];

        passengerItems.push({
            text: i18n.global.t(`passenger.${resource[0].type}`, resource[0].amount),
            amount: resource[0].amount,
            type: resource[0].type,
            price: resource[0].price
        } as BookingItem);
    }

    //we also want to populate dogs in passengerlist
    const dogs = items.find(x => x.code === "PETC");
    if(dogs) {
        passengerItems.push({
            text: i18n.global.t(`passenger.${dogs.code}`, dogs.amount),
            amount: dogs.amount,
            type: dogs.type,
            price: dogs.price
        } as BookingItem);
    }
    
    return passengerItems;
}

const getRouteTranslations = (route: string, modeOfTravel: ModeOfTravelEnum) => {
    const bookingSummary = useBookingSummary();
    return bookingSummary.getRouteTranslations(route, modeOfTravel);
}